// pages/ApiKeys.jsx
import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { FiCopy, FiTrash2, FiPlus, FiCheck, FiX, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';

function ApiKeys() {
  const { token, user, isAuthenticated, loading: authLoading } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState(['read']);
  const [copiedKey, setCopiedKey] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Log authentication state for debugging
  useEffect(() => {
    console.log('ApiKeys: Auth state', { isAuthenticated, token, user, authLoading });
  }, [isAuthenticated, token, user, authLoading]);

  useEffect(() => {
    if (authLoading) {
      console.log('ApiKeys: Auth still loading...');
      return;
    }
    
    if (isAuthenticated && token) {
      console.log('ApiKeys: Authenticated, fetching keys...');
      fetchKeys();
    } else {
      console.log('ApiKeys: Not authenticated, setting loading false');
      setLoading(false);
    }
  }, [isAuthenticated, token, authLoading]);

  const fetchKeys = async () => {
    if (!token) {
      console.log('ApiKeys: No token, cannot fetch');
      setLoading(false);
      return;
    }

    console.log('ApiKeys: Fetching keys with token:', token.substring(0, 20) + '...');
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/developer/api-keys');
      console.log('ApiKeys: Fetch response', response.data);
      
      if (response.data.success) {
        setKeys(response.data.keys || []);
      } else {
        setKeys([]);
        if (response.data.error) {
          setError(response.data.error);
        }
      }
    } catch (error) {
      console.error('ApiKeys: Error fetching API keys:', error);
      
      if (error.response?.status === 401) {
        setError('Session expired. Please login again.');
      } else if (error.response?.status === 404) {
        setKeys([]);
        setError('API key endpoint not found. Please check server.');
      } else if (error.code === 'ERR_NETWORK') {
        setError('Cannot connect to server. Please check if the worker is running.');
      } else {
        setError('Unable to load API keys. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a key name');
      return;
    }

    if (!token) {
      toast.error('Please login first');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.post('/api/developer/api-keys', {
        name: newKeyName.trim(),
        permissions: newKeyPermissions,
      });

      if (response.data.success) {
        const newKey = response.data.key;
        setKeys([newKey, ...keys]);
        setShowCreateModal(false);
        setNewKeyName('');
        setNewKeyPermissions(['read']);
        toast.success('API Key generated successfully!');
        
        if (newKey.key) {
          setCopiedKey(newKey.key);
          setTimeout(() => setCopiedKey(null), 10000);
        }
      } else {
        toast.error(response.data.error || 'Failed to generate API key');
      }
    } catch (error) {
      console.error('Error generating API key:', error);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else if (error.response?.status === 409) {
        toast.error('A key with this name already exists');
      } else {
        toast.error('Failed to generate API key. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const deleteKey = async (id) => {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    setDeletingId(id);
    try {
      const response = await api.delete(`/api/developer/api-keys/${id}`);

      if (response.data.success) {
        setKeys(keys.filter(k => k.id !== id));
        toast.success('API Key deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error('Failed to delete API key. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleKeyStatus = async (id) => {
    const key = keys.find(k => k.id === id);
    if (!key) return;

    const newStatus = !key.active;

    try {
      const response = await api.patch(`/api/developer/api-keys/${id}`, {
        active: newStatus
      });

      if (response.data.success) {
        setKeys(keys.map(k => k.id === id ? { ...k, active: newStatus } : k));
        toast.success(`Key ${newStatus ? 'activated' : 'deactivated'} successfully`);
      }
    } catch (error) {
      console.error('Error toggling API key status:', error);
      toast.error('Failed to update API key status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(text);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedKey(null), 3000);
    }).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedKey(text);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedKey(null), 3000);
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // If auth is still loading, show loading
  if (authLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Checking authentication...</p>
      </div>
    );
  }

  // If not authenticated, show login prompt
  if (!isAuthenticated || !token) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
          Please login to manage API keys.
        </p>
        <button className="btn btn-primary" onClick={() => window.location.href = '/login'}>
          Go to Login
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading API keys...</p>
      </div>
    );
  }

  if (error && keys.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
          {error}
        </p>
        <button className="btn btn-primary" onClick={fetchKeys}>
          <FiRefreshCw /> Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px', 
        flexWrap: 'wrap', 
        gap: '12px' 
      }}>
        <div>
          <h2 style={{ margin: 0 }}>API Keys</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Manage your API keys for accessing the Delivery7 API
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <FiPlus /> Generate New Key
        </button>
      </div>

      <div className="card">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '12px',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
            {keys.length} {keys.length === 1 ? 'key' : 'keys'} found
          </span>
          <button 
            className="btn btn-outline" 
            onClick={fetchKeys} 
            style={{ padding: '4px 12px', fontSize: '12px' }}
            disabled={loading}
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>

        <div className="table-responsive">
          {keys.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              color: 'var(--text-muted)', 
              padding: '40px 20px' 
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔑</div>
              <h3 style={{ marginBottom: '8px' }}>No API Keys Yet</h3>
              <p style={{ marginBottom: '16px' }}>
                Generate your first API key to start using the Delivery7 API
              </p>
              <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                <FiPlus /> Generate Your First Key
              </button>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>API Key</th>
                  <th>Created</th>
                  <th>Last Used</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map(key => (
                  <tr key={key.id}>
                    <td>
                      <strong>{key.name}</strong>
                      {key.permissions && (
                        <div style={{ 
                          fontSize: '10px', 
                          color: 'var(--text-muted)',
                          marginTop: '2px'
                        }}>
                          {key.permissions.join(', ')}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="api-key-display">
                        <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                          {key.key}
                        </span>
                        <button 
                          className="copy-btn" 
                          onClick={() => copyToClipboard(key.key)}
                          title="Copy to clipboard"
                        >
                          {copiedKey === key.key ? <FiCheck /> : <FiCopy />}
                        </button>
                      </div>
                    </td>
                    <td style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      {formatDate(key.createdAt)}
                    </td>
                    <td style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      {formatDate(key.lastUsed)}
                    </td>
                    <td>
                      <span className={`badge ${key.active !== false ? 'badge-active' : 'badge-inactive'}`}>
                        {key.active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button 
                          className="btn btn-outline btn-sm" 
                          onClick={() => toggleKeyStatus(key.id)}
                          disabled={deletingId === key.id}
                        >
                          {key.active !== false ? 'Deactivate' : 'Activate'}
                        </button>
                        <button 
                          className="btn btn-danger btn-sm" 
                          onClick={() => deleteKey(key.id)}
                          disabled={deletingId === key.id}
                        >
                          {deletingId === key.id ? '...' : <FiTrash2 />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Generate New API Key</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowCreateModal(false)}
              >
                <FiX />
              </button>
            </div>
            
            <div className="form-group">
              <label>Key Name <span style={{ color: 'var(--danger)' }}>*</span></label>
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g., Production Key"
                autoFocus
                maxLength={50}
              />
              <div className="form-hint">
                {newKeyName.length}/50 characters
              </div>
            </div>
            
            <div className="form-group">
              <label>Permissions</label>
              <select
                multiple
                value={newKeyPermissions}
                onChange={(e) => {
                  const values = Array.from(e.target.selectedOptions, option => option.value);
                  setNewKeyPermissions(values.length ? values : ['read']);
                }}
                style={{ height: '80px' }}
              >
                <option value="read">Read - View data</option>
                <option value="write">Write - Create/Update data</option>
                <option value="admin">Admin - Full access</option>
              </select>
              <div className="form-hint">
                Hold Ctrl (Windows) or Cmd (Mac) to select multiple
              </div>
            </div>

            <div style={{ 
              background: 'var(--bg-input)', 
              padding: '12px', 
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: 'var(--text-secondary)'
            }}>
              <strong>⚠️ Important:</strong> This key will only be shown once. 
              Make sure to copy it immediately.
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                className="btn btn-primary" 
                onClick={generateKey}
                disabled={generating || !newKeyName.trim()}
                style={{ flex: 1 }}
              >
                {generating ? 'Generating...' : 'Generate Key'}
              </button>
              <button 
                className="btn btn-outline" 
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApiKeys;